import { useCallback, useEffect, useState } from "react";
import { Check, FolderPlus, RefreshCw, X } from "lucide-react";
import { api, type Aufgabe, type Folder } from "../api";
import { ordnerPfad } from "../lib/ordner";
import { datumDe, fehlerText, toast } from "./verwaltung";

interface Props {
  folders: Folder[];
  /** Nach einer Entscheidung: Zähler, Ordnerbaum und Liste neu laden. */
  onChanged: () => void;
}

/**
 * Aufgaben: Vorschläge des Agenten, die nur mit deinem Ja umgesetzt werden —
 * etwa „Neuer Kunde: Ordner und Regel anlegen?". Der Ordnername lässt sich
 * vor dem Ja noch anpassen.
 */
export function AufgabenPage({ folders, onChanged }: Props) {
  const [offen, setOffen] = useState<Aufgabe[] | null>(null);
  const [erledigt, setErledigt] = useState<Aufgabe[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [namen, setNamen] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const [o, alle] = await Promise.all([api.aufgaben("offen"), api.aufgaben("alle")]);
      setOffen(o);
      setErledigt(alle.filter((a) => a.status !== "offen").slice(0, 20));
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setLaedt(false);
    }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  /** „INBOX.Kunden" → „Posteingang / Kunden", mit den Anzeigenamen des Ordnerbaums. */
  const elternAnzeige = (a: Aufgabe) => {
    const f = folders.find((x) => x.account_id === a.accountId && x.path === a.daten.elternPfad);
    if (f) return ordnerPfad(f, folders);
    return a.daten.elternPfad.split(a.daten.delimiter).map((t) => (t.toUpperCase() === "INBOX" ? "Posteingang" : t)).join(" / ");
  };

  const entscheiden = async (a: Aufgabe, ja: boolean) => {
    setBusy(a.id);
    try {
      if (ja) {
        const name = (namen[a.id] ?? a.daten.ordnerName).trim();
        const r = await api.aufgabeAnnehmen(a.id, name);
        toast.erfolg(
          `Ordner „${name}" und Regel angelegt${r.verschoben ? ` — ${r.verschoben} Mail${r.verschoben === 1 ? "" : "s"} verschoben` : ""}.`,
        );
      } else {
        await api.aufgabeAblehnen(a.id);
        toast(`Kein Ordner für ${a.daten.name} — es wird nicht erneut gefragt.`);
      }
      onChanged();
    } catch (e) {
      toast.fehler(e);
    } finally {
      setBusy(null);
      void laden();
    }
  };

  return (
    <section className="pane wide" data-active="true">
      <header className="pane-head">
        <span className="pane-title">Aufgaben</span>
        <div className="spacer" />
        <button className="icon-btn" onClick={() => void laden()} disabled={laedt} aria-label="Neu laden" title="Neu laden">
          <RefreshCw aria-hidden="true" />
        </button>
      </header>

      <div className="pane-scroll">
        <div className="page">
          <h2>Aufgaben</h2>
          <p className="lede">
            Schreibt ein neuer Kunde, schlägt der Agent einen eigenen Ordner samt Regel vor. Mit <b>Ja</b> wird beides angelegt
            und die bisherigen Mails wandern mit. Bei <b>Nein</b> fragt er für diesen Absender nicht wieder.
          </p>

          {fehler && (
            <div className="v-leer" data-art="fehler">
              <strong>Die Aufgaben ließen sich nicht laden</strong>
              <span>{fehler}</span>
              <button className="btn" onClick={() => void laden()}>Erneut versuchen</button>
            </div>
          )}

          {!fehler && offen === null && <div className="v-laden" role="status">Lade Aufgaben …</div>}

          {offen?.length === 0 && (
            <div className="v-leer">
              <FolderPlus aria-hidden="true" />
              <strong>Nichts zu entscheiden</strong>
              <span>Sobald ein neuer Kunde schreibt, erscheint hier der Vorschlag für seinen Ordner.</span>
            </div>
          )}

          {offen?.map((a) => {
            const name = namen[a.id] ?? a.daten.ordnerName;
            return (
              <div className="proposal aufgabe" key={a.id}>
                <div className="proposal-text">
                  Neuer Kunde: <b>{a.daten.name}</b>
                  {a.daten.name !== a.daten.email && <> &lt;{a.daten.email}&gt;</>}
                  <span className="proposal-quelle">
                    „{a.daten.betreff || "(ohne Betreff)"}" · {datumDe(a.createdAt)} · Regel:{" "}
                    {a.daten.feld === "from" ? `Absender ist ${a.daten.wert}` : `alle Absender von @${a.daten.wert}`}
                  </span>
                  <label className="aufgabe-ordner">
                    <span>{elternAnzeige(a)} /</span>
                    <input
                      className="input"
                      value={name}
                      aria-label="Name des Kundenordners"
                      disabled={busy === a.id}
                      onChange={(e) => setNamen((n) => ({ ...n, [a.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) void entscheiden(a, true); }}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="btn primary"
                  disabled={busy !== null || !name.trim()}
                  onClick={() => void entscheiden(a, true)}
                >
                  <Check aria-hidden="true" /> Ja
                </button>
                <button type="button" className="btn" disabled={busy !== null} onClick={() => void entscheiden(a, false)}>
                  <X aria-hidden="true" /> Nein
                </button>
              </div>
            );
          })}

          {erledigt.length > 0 && (
            <div className="v-abschnitt">
              <h3>Zuletzt entschieden</h3>
              {erledigt.map((a) => (
                <div className="proposal done" key={a.id}>
                  <span className="proposal-text">
                    {a.status === "erledigt" ? "Angelegt" : "Abgelehnt"}: <b>{a.daten.name}</b>
                    <span className="proposal-quelle">
                      {a.status === "erledigt" && a.ergebnis?.ordner
                        ? `Ordner ${a.ergebnis.ordner.split(a.daten.delimiter).pop()}${a.ergebnis.verschoben ? `, ${a.ergebnis.verschoben} Mails verschoben` : ""} · `
                        : ""}
                      {datumDe(a.decidedAt)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
